'use client';
import React, { useMemo, useState } from 'react';
import { cn } from '@/lib/cn';
import { IconAlertCircleFilled, IconBroom, IconStarFilled } from '@repo/svgs';

export interface ReviewRatingCellProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  label: string;
  value: number | null;
  onChange: (next: number) => void;
  icon?: React.ComponentType<{ className?: string }>;
  alert?: boolean;
  disabled?: boolean;
}

const STARS = [1, 2, 3, 4, 5] as const;

export function ReviewRatingCell({
  label,
  value,
  onChange,
  icon: Icon = IconBroom,
  alert = false,
  disabled = false,
  className,
  ...rest
}: ReviewRatingCellProps) {
  const [hoverValue, setHoverValue] = useState<number | null>(null);
  const showAlert = alert && !disabled;

  const currentValue = useMemo(
    () => (hoverValue !== null ? hoverValue : value),
    [hoverValue, value],
  );

  const handleSelect = (next: number) => {
    if (disabled) return;
    onChange(next);
  };

  return (
    <div
      className={cn(
        'flex items-center gap-4 rounded-2xl px-2 py-3',
        'bg-primary cursor-pointer',
        disabled && 'bg-secondary cursor-not-allowed',
        showAlert ? 'border border-alert hover:bg-alert-hover' : 'hover:bg-primary-hover',
        className,
      )}
      role="radiogroup"
      aria-label={label}
      aria-disabled={disabled || undefined}
      aria-invalid={showAlert || undefined}
      onMouseLeave={() => setHoverValue(null)}
      {...rest}
    >
      <Icon className={cn('w-6 h-6 shrink-0', disabled ? 'icon-tertiary' : 'icon-secondary')} />

      <div className="flex flex-1 gap-4 justify-between">
        <p className={cn('text-body-2 text-primary', disabled && 'text-tertiary')}>{label}</p>

        <div className="flex items-center gap-1">
          {STARS.map(star => {
            const filled = currentValue !== null && star <= currentValue;

            return (
              <button
                key={star}
                type="button"
                className={cn(
                  'p-0.5 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                  !disabled && 'cursor-pointer',
                  disabled && 'cursor-not-allowed',
                )}
                role="radio"
                aria-label={`${label} ${star} star${star > 1 ? 's' : ''}`}
                aria-checked={value !== null && star === value}
                tabIndex={star === 1 ? 0 : -1}
                disabled={disabled}
                onMouseEnter={() => {
                  if (!disabled) setHoverValue(star);
                }}
                onFocus={() => {
                  if (!disabled) setHoverValue(null);
                }}
                onClick={() => handleSelect(star)}
              >
                <IconStarFilled
                  className={cn(
                    'w-5 h-5 transition-colors',
                    disabled ? 'icon-tertiary' : filled ? 'icon-rating' : 'icon-tertiary',
                  )}
                />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default ReviewRatingCell;
